import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Eye,
  EyeOff,
  Save,
  ExternalLink,
  Link2,
  Banknote,
  TrendingUp,
  Webhook,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import SuperAdminCompanySelector, { useSuperAdminCompany } from '@/components/SuperAdminCompanySelector';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MamoPayIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingMamo, setSavingMamo] = useState(false);
  const [testingMamo, setTestingMamo] = useState(false);
  const [showMamoKey, setShowMamoKey] = useState(false);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [loadingWebhookStatus, setLoadingWebhookStatus] = useState(false);
  
  // Super Admin company selector
  const { 
    selectedCompanyId, 
    setSelectedCompanyId, 
    needsCompanySelector, 
    isReady 
  } = useSuperAdminCompany();
  
  const [mamoSettings, setMamoSettings] = useState({
    configured: false,
    api_key: '',
    api_key_masked: '',
    enable_for_leads: true,
    enable_for_orders: true,
    enable_for_drivers: true,
    send_customer_receipt: true,
    default_description_template: 'Payment for {reference}'
  });
  
  const [webhookStatus, setWebhookStatus] = useState({
    configured: false,
    webhook_id: null,
    webhook_url: null,
    expected_webhook_url: 'https://opswarex.app/api/mamopay/webhook',
    registered_at: null
  });
  
  const [stats, setStats] = useState({
    totalLinks: 0,
    paidLinks: 0,
    pendingLinks: 0,
    totalAmount: 0
  });
  
  useEffect(() => {
    if (isReady) {
      loadMamoSettings();
      loadStats();
    } else {
      setLoading(false);
    }
  }, [isReady, selectedCompanyId]);
  
  const loadMamoSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/settings/mamopay`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMamoSettings(response.data);
      
      // Also load webhook status if MamoPay is configured
      if (response.data.configured) {
        loadWebhookStatus();
      }
    } catch (error) {
      console.error('Failed to load MamoPay settings');
    } finally {
      setLoading(false);
    }
  };
  
  const loadWebhookStatus = async () => {
    setLoadingWebhookStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mamopay/webhook/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhookStatus({
        configured: !!(response.data.configured_webhook_id || response.data.configured_webhook_url),
        webhook_id: response.data.configured_webhook_id,
        webhook_url: response.data.configured_webhook_url,
        expected_webhook_url: response.data.expected_webhook_url || 'https://opswarex.app/api/mamopay/webhook',
        registered_at: response.data.registered_at,
        mamopay_webhooks: response.data.mamopay_webhooks || []
      });
    } catch (error) {
      console.error('Failed to load webhook status');
    } finally {
      setLoadingWebhookStatus(false);
    }
  };
  
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mamopay/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      // Stats endpoint might not exist
      console.log('Stats not available');
    }
  };
  
  const handleSaveMamoSettings = async () => {
    if (!mamoSettings.api_key && !mamoSettings.configured) {
      toast.error('Please enter your MamoPay API key');
      return;
    }
    
    // Super admin must select a company
    if (needsCompanySelector && !selectedCompanyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setSavingMamo(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/settings/mamopay`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      await axios.put(url, {
        api_key: mamoSettings.api_key,
        enable_for_leads: mamoSettings.enable_for_leads,
        enable_for_orders: mamoSettings.enable_for_orders,
        enable_for_drivers: mamoSettings.enable_for_drivers,
        send_customer_receipt: mamoSettings.send_customer_receipt,
        default_description_template: mamoSettings.default_description_template
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MamoPay settings saved successfully');
      loadMamoSettings(); // Reload to get updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MamoPay settings');
    } finally {
      setSavingMamo(false);
    }
  };
  
  const handleTestMamoConnection = async () => {
    setTestingMamo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/mamopay/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Connection successful! MamoPay API is working.');
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingMamo(false);
    }
  };
  
  const handleRegisterWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/mamopay/webhook/register`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Webhook registered successfully! MamoPay will now send payment notifications automatically.');
      loadWebhookStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register webhook');
    } finally {
      setRegisteringWebhook(false);
    }
  };
  
  const handleDeleteWebhook = async () => {
    if (!webhookStatus.webhook_id) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/mamopay/webhook/${webhookStatus.webhook_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Webhook removed successfully');
      loadWebhookStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove webhook');
    }
  };
  
  const isConnected = mamoSettings.configured;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/integrations')}
          className="text-wt-text-muted hover:text-wt-text-primary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Integrations
        </Button>
      </div>
      
      {/* Super Admin Company Selector */}
      <SuperAdminCompanySelector
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={setSelectedCompanyId}
        integrationName="MamoPay"
      />
      
      {/* Show message if super admin hasn't selected a company */}
      {needsCompanySelector && !selectedCompanyId ? (
        <div className="text-center py-12 text-wt-text-muted">
          <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Please select a company above to configure MamoPay integration.</p>
        </div>
      ) : (
      <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
            <CreditCard className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">MamoPay Integration</h1>
            <p className="text-wt-text-muted mt-1">Accept payments and generate payment links</p>
          </div>
        </div>
        <Badge 
          className={isConnected 
            ? 'bg-green-500/20 text-green-400 border-green-500/30 text-sm px-3 py-1' 
            : 'bg-gray-500/20 text-gray-400 border-gray-500/30 text-sm px-3 py-1'
          }
        >
          {isConnected ? (
            <><CheckCircle className="h-4 w-4 mr-1" /> Connected</>
          ) : (
            <><XCircle className="h-4 w-4 mr-1" /> Not Connected</>
          )}
        </Badge>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Link2 className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.totalLinks || 0}</p>
              <p className="text-sm text-wt-text-muted">Total Links</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.paidLinks || 0}</p>
              <p className="text-sm text-wt-text-muted">Paid Links</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <TrendingUp className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.pendingLinks || 0}</p>
              <p className="text-sm text-wt-text-muted">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-wt-gold/20 rounded-lg">
              <Banknote className="h-5 w-5 text-wt-gold" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{(stats.totalAmount || 0).toLocaleString()} د.إ</p>
              <p className="text-sm text-wt-text-muted">Total Collected</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h2 className="text-lg font-semibold text-wt-text-primary mb-4">API Configuration</h2>
          
          <div className="space-y-4">
            {/* API Key */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showMamoKey ? "text" : "password"}
                    placeholder={mamoSettings.configured ? mamoSettings.api_key_masked : "Enter your MamoPay API key"}
                    value={mamoSettings.api_key}
                    onChange={(e) => setMamoSettings({ ...mamoSettings, api_key: e.target.value })}
                    className="bg-wt-paper border-wt-border text-wt-text-primary pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowMamoKey(!showMamoKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                  >
                    {showMamoKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {mamoSettings.configured && (
                  <Button
                    variant="outline"
                    onClick={handleTestMamoConnection}
                    disabled={testingMamo}
                    className="border-wt-border text-wt-text-primary"
                  >
                    {testingMamo ? 'Testing...' : 'Test'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-wt-text-muted mt-1">
                Get your API key from <a href="https://business.mamopay.com" target="_blank" rel="noopener noreferrer" className="text-wt-gold hover:underline">MamoPay Dashboard</a> → Settings → API
              </p>
            </div>

            {/* Feature Toggles */}
            <div className="grid grid-cols-1 gap-4 pt-4 border-t border-wt-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Enable for Leads</Label>
                  <p className="text-xs text-wt-text-muted">Create payment links from lead details</p>
                </div>
                <Switch
                  checked={mamoSettings.enable_for_leads}
                  onCheckedChange={(checked) => setMamoSettings({ ...mamoSettings, enable_for_leads: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Enable for Orders</Label>
                  <p className="text-xs text-wt-text-muted">Create payment links from order details</p>
                </div>
                <Switch
                  checked={mamoSettings.enable_for_orders}
                  onCheckedChange={(checked) => setMamoSettings({ ...mamoSettings, enable_for_orders: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Enable for Drivers</Label>
                  <p className="text-xs text-wt-text-muted">Allow drivers to create payment links</p>
                </div>
                <Switch
                  checked={mamoSettings.enable_for_drivers}
                  onCheckedChange={(checked) => setMamoSettings({ ...mamoSettings, enable_for_drivers: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Send Customer Receipt</Label>
                  <p className="text-xs text-wt-text-muted">Email receipt to customer after payment</p>
                </div>
                <Switch
                  checked={mamoSettings.send_customer_receipt}
                  onCheckedChange={(checked) => setMamoSettings({ ...mamoSettings, send_customer_receipt: checked })}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-wt-border">
              <Button
                onClick={handleSaveMamoSettings}
                disabled={savingMamo}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingMamo ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </motion.div>
        
        {/* Webhook Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Webhook Configuration</h2>
          
          {!mamoSettings.configured ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-wt-text-muted mx-auto mb-3" />
              <p className="text-wt-text-muted">Configure your API key first to set up webhooks</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-wt-gold" />
                  <Label className="text-wt-text-primary font-medium">Payment Webhook</Label>
                </div>
                {loadingWebhookStatus ? (
                  <Loader2 className="h-4 w-4 animate-spin text-wt-text-muted" />
                ) : webhookStatus.configured ? (
                  <span className="flex items-center gap-1 text-green-400 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-yellow-400 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    Not Registered
                  </span>
                )}
              </div>
              
              <p className="text-sm text-wt-text-muted">
                Webhooks automatically update order status when customers complete payments. This ensures orders are marked as paid in real-time.
              </p>
              
              {webhookStatus.configured ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-400 font-medium">Webhook Active</p>
                      <p className="text-xs text-wt-text-muted mt-1 break-all">
                        Registered URL: {webhookStatus.webhook_url}
                      </p>
                      <p className="text-xs text-wt-text-muted mt-1 break-all">
                        Expected URL: {webhookStatus.expected_webhook_url}
                      </p>
                      {webhookStatus.webhook_url !== webhookStatus.expected_webhook_url && (
                        <p className="text-xs text-yellow-400 mt-1">
                          ⚠️ Registered URL differs from expected. Consider re-registering.
                        </p>
                      )}
                      {webhookStatus.registered_at && (
                        <p className="text-xs text-wt-text-muted">
                          Registered: {new Date(webhookStatus.registered_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteWebhook}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-sm text-yellow-400 font-medium">Webhook Not Registered</p>
                      <p className="text-xs text-wt-text-muted mt-1">
                        Register a webhook to receive automatic payment notifications from MamoPay.
                      </p>
                      <p className="text-xs text-wt-text-muted mt-1 break-all">
                        Will register: {webhookStatus.expected_webhook_url}
                      </p>
                    </div>
                    <Button
                      onClick={handleRegisterWebhook}
                      disabled={registeringWebhook}
                      className="bg-wt-gold hover:bg-wt-gold-dark text-black w-full"
                      data-testid="register-webhook-btn"
                    >
                      {registeringWebhook ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Registering...
                        </>
                      ) : (
                        <>
                          <Webhook className="h-4 w-4 mr-2" />
                          Register Webhook
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* How It Works */}
              <div className="pt-4 border-t border-wt-border">
                <h3 className="text-sm font-medium text-wt-text-primary mb-3">How It Works</h3>
                <div className="space-y-2 text-sm text-wt-text-muted">
                  <div className="flex items-start gap-2">
                    <span className="text-wt-gold font-semibold">1.</span>
                    <span>Open an order and click "Send Payment Link"</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-wt-gold font-semibold">2.</span>
                    <span>Share the link with customer via WhatsApp or copy</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-wt-gold font-semibold">3.</span>
                    <span>Customer pays online</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-400 font-semibold">4.</span>
                    <span>Order is automatically marked as paid</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
      
      {/* Documentation Link */}
      <div className="flex justify-center">
        <Button
          variant="link"
          onClick={() => window.open('https://mamopay.com/docs', '_blank')}
          className="text-wt-text-muted hover:text-wt-gold"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View MamoPay Documentation
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

export default MamoPayIntegrationPage;
